import {jsPDF} from 'jspdf'
import 'jspdf-autotable'
import type {SurveyReportQuestionSummary, SurveyReportSummary} from './surveyReportUtils'

interface DownloadSurveyReportPdfArgs {
  summary: SurveyReportSummary
  aiAnalysis?: string | null
}

const page = {
  width: 297,
  height: 210,
  margin: 12,
}

const colors = {
  primary: [35, 83, 155],
  muted: [95, 99, 117],
  border: [224, 227, 235],
  light: [247, 249, 252],
  success: [30, 130, 76],
  warning: [210, 126, 0],
}

const FONT_NAME = 'Roboto'
let fontLoadPromise: Promise<{regular: string; bold: string} | null> | null = null

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const chunkSize = 0x8000

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode.apply(null, Array.from(chunk))
  }

  return btoa(binary)
}

const loadRobotoFonts = async (doc: jsPDF) => {
  if (!fontLoadPromise) {
    fontLoadPromise = Promise.all([
      fetch(`${process.env.PUBLIC_URL || ''}/fonts/Roboto-Regular.ttf`).then((res) => res.arrayBuffer()),
      fetch(`${process.env.PUBLIC_URL || ''}/fonts/Roboto-Bold.ttf`).then((res) => res.arrayBuffer()),
    ])
      .then(([regular, bold]) => {
        return {
          regular: arrayBufferToBase64(regular),
          bold: arrayBufferToBase64(bold),
        }
      })
      .catch(() => null)
  }

  const fonts = await fontLoadPromise
  if (fonts) {
    doc.addFileToVFS('Roboto-Regular.ttf', fonts.regular)
    doc.addFont('Roboto-Regular.ttf', FONT_NAME, 'normal')
    doc.addFileToVFS('Roboto-Bold.ttf', fonts.bold)
    doc.addFont('Roboto-Bold.ttf', FONT_NAME, 'bold')
    doc.setFont(FONT_NAME, 'normal')
  }
  return !!fonts
}

const formatPercent = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(1)}%` : '-'

const formatAverage = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(1) : '-'

const safeFileName = (value?: string | null) =>
  (value || 'anket-raporu')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80) || 'anket-raporu'

const getDisplayTitle = (item: SurveyReportQuestionSummary) =>
  item.rowTitle ? `${item.questionTitle} - ${item.rowTitle}` : item.questionTitle

const ensureSpace = (doc: jsPDF, y: number, required = 18) => {
  if (y + required <= page.height - page.margin) return y
  doc.addPage()
  return page.margin
}

const addFooter = (doc: jsPDF) => {
  const pageCount = doc.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2])
    doc.text(`Sayfa ${i}/${pageCount}`, page.width - page.margin, page.height - 6, {align: 'right'})
  }
}

const addWrappedText = (
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight = 5
) => {
  const lines = doc.splitTextToSize(text || '-', maxWidth)
  doc.text(lines, x, y)
  return y + lines.length * lineHeight
}

const addSectionTitle = (doc: jsPDF, title: string, y: number) => {
  y = ensureSpace(doc, y, 14)
  doc.setFont(FONT_NAME, 'bold')
  doc.setFontSize(13)
  doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2])
  doc.text(title, page.margin, y)
  doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2])
  doc.line(page.margin, y + 2, page.width - page.margin, y + 2)
  doc.setFont(FONT_NAME, 'normal')
  doc.setTextColor(33, 37, 41)
  return y + 8
}

const addQuestionListTable = (
  doc: jsPDF,
  title: string,
  items: SurveyReportQuestionSummary[],
  y: number
) => {
  if (!items.length) return y
  y = addSectionTitle(doc, title, y)

  ;(doc as any).autoTable({
    startY: y,
    head: [['Soru', 'Cevap', 'Ortalama']],
    body: items.map((item) => [
      getDisplayTitle(item),
      item.answerCount.toString(),
      formatAverage(item.normalizedAverage),
    ]),
    margin: {left: page.margin, right: page.margin},
    styles: {
      font: FONT_NAME,
      fontSize: 8,
      cellPadding: 2,
      minCellWidth: 0,
      overflow: 'linebreak',
      valign: 'top',
    },
    headStyles: {
      fillColor: colors.primary,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    columnStyles: {
      0: {cellWidth: 205},
      1: {cellWidth: 22, halign: 'right'},
      2: {cellWidth: 28, halign: 'right'},
    },
  })

  return ((doc as any).lastAutoTable?.finalY || y) + 8
}

const addFrequencyTable = (doc: jsPDF, item: SurveyReportQuestionSummary, y: number) => {
  y = ensureSpace(doc, y, 34)
  const title = getDisplayTitle(item)

  doc.setFont(FONT_NAME, 'bold')
  doc.setFontSize(10)
  doc.setTextColor(33, 37, 41)
  y = addWrappedText(doc, title, page.margin, y, page.width - page.margin * 2, 4.5)
  doc.setFont(FONT_NAME, 'normal')
  doc.setFontSize(8)
  doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2])
  doc.text(
    `Cevap: ${item.answerCount}/${item.totalResponseCount} | Ortalama: ${formatAverage(item.normalizedAverage)}`,
    page.margin,
    y
  )
  y += 3

  ;(doc as any).autoTable({
    startY: y,
    head: [['Seçenek', 'Sayı', 'Yüzde']],
    body: item.options.map((option) => [
      option.label,
      option.count.toString(),
      formatPercent(option.percentage),
    ]),
    margin: {left: page.margin, right: page.margin},
    styles: {
      font: FONT_NAME,
      fontSize: 8,
      cellPadding: 1.8,
      minCellWidth: 0,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: colors.light,
      textColor: [33, 37, 41],
      fontStyle: 'bold',
    },
    alternateRowStyles: {fillColor: [252, 253, 255]},
    columnStyles: {
      0: {cellWidth: 200},
      1: {cellWidth: 22, halign: 'right'},
      2: {cellWidth: 24, halign: 'right'},
    },
  })

  return ((doc as any).lastAutoTable?.finalY || y) + 7
}

const getOpenEndedAnswers = (item: SurveyReportQuestionSummary) =>
  item.answers && item.answers.length > 0 ? item.answers : item.sampleAnswers || []

const addOpenEndedTable = (doc: jsPDF, item: SurveyReportQuestionSummary, y: number) => {
  y = ensureSpace(doc, y, 34)

  doc.setFont(FONT_NAME, 'bold')
  doc.setFontSize(10)
  doc.setTextColor(33, 37, 41)
  y = addWrappedText(doc, getDisplayTitle(item), page.margin, y, page.width - page.margin * 2, 4.5)

  doc.setFont(FONT_NAME, 'normal')
  doc.setFontSize(8)
  doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2])
  doc.text(`Yanıt: ${item.answerCount}/${item.totalResponseCount}`, page.margin, y)
  y += 3

  const answers = getOpenEndedAnswers(item)

  ;(doc as any).autoTable({
    startY: y,
    head: [['#', 'Yanıt']],
    body: answers.length > 0 ? answers.map((answer, index) => [(index + 1).toString(), answer]) : [['-', '-']],
    margin: {left: page.margin, right: page.margin},
    styles: {
      font: FONT_NAME,
      fontSize: 8,
      cellPadding: 2,
      minCellWidth: 0,
      overflow: 'linebreak',
      valign: 'top',
    },
    headStyles: {
      fillColor: colors.primary,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    alternateRowStyles: {fillColor: [252, 253, 255]},
    columnStyles: {
      0: {cellWidth: 12, halign: 'right'},
      1: {cellWidth: 241},
    },
    pageBreak: 'auto',
    rowPageBreak: 'auto',
  })

  return ((doc as any).lastAutoTable?.finalY || y) + 8
}

export const downloadSurveyReportPdf = async ({summary, aiAnalysis}: DownloadSurveyReportPdfArgs) => {
  const doc = new jsPDF({orientation: 'landscape', unit: 'mm', format: 'a4'})
  await loadRobotoFonts(doc)

  let y = page.margin

  doc.setFont(FONT_NAME, 'bold')
  doc.setFontSize(18)
  doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2])
  y = addWrappedText(doc, 'Anket Sonuç Raporu', page.margin, y, page.width - page.margin * 2, 7)

  doc.setFontSize(13)
  doc.setTextColor(33, 37, 41)
  y = addWrappedText(doc, summary.surveyName, page.margin, y + 2, page.width - page.margin * 2, 6)

  doc.setFont(FONT_NAME, 'normal')
  doc.setFontSize(9)
  doc.setTextColor(colors.muted[0], colors.muted[1], colors.muted[2])
  y += 2
  doc.text(`Dönem: ${summary.semesterName || '-'}`, page.margin, y)
  doc.text(`Rapor Tarihi: ${summary.generatedAt || '-'}`, page.margin + 90, y)
  doc.text(`Anket Tarihi: ${summary.surveyEndAt || '-'}`, page.margin + 185, y)
  y += 6

  ;(doc as any).autoTable({
    startY: y,
    body: [
      ['Tamamlanan Anket', summary.responseCount.toString()],
      ['Tekil Katılımcı', summary.uniqueParticipantCount.toString()],
      ['Genel Ortalama', formatAverage(summary.overallAverage)],
      ['Likert/Frekans Kalemi', summary.likertItems.length.toString()],
    ],
    margin: {left: page.margin, right: page.margin},
    theme: 'grid',
    styles: {
      font: FONT_NAME,
      fontSize: 9,
      cellPadding: 2.5,
      minCellWidth: 0,
      overflow: 'linebreak',
    },
    columnStyles: {
      0: {fontStyle: 'bold', fillColor: colors.light, cellWidth: 58},
      1: {cellWidth: 35},
    },
    tableWidth: 'wrap',
  })
  y = ((doc as any).lastAutoTable?.finalY || y) + 8

  y = addQuestionListTable(doc, 'En Güçlü Alanlar', summary.strongestItems, y)
  y = addQuestionListTable(doc, 'Gelişime Açık Alanlar', summary.improvementItems, y)

  if (aiAnalysis) {
    y = addSectionTitle(doc, 'Yapay Zeka Yorumu', y)
    doc.setFont(FONT_NAME, 'normal')
    doc.setFontSize(9)
    doc.setTextColor(33, 37, 41)
    const paragraphs = aiAnalysis
      .replace(/\*\*/g, '')
      .split(/\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 18)

    paragraphs.forEach((paragraph) => {
      y = ensureSpace(doc, y, 12)
      y = addWrappedText(doc, paragraph, page.margin, y, page.width - page.margin * 2, 4.8) + 1.5
    })
  }

  if (summary.likertItems.length > 0) {
    doc.addPage()
    y = page.margin
    y = addSectionTitle(doc, 'Likert Soru Frekansları', y)
    summary.likertItems.forEach((item) => {
      y = addFrequencyTable(doc, item, y)
    })
  }

  if (summary.choiceItems.length > 0) {
    y = addSectionTitle(doc, 'Diğer Seçmeli Soru Frekansları', y)
    summary.choiceItems.forEach((item) => {
      y = addFrequencyTable(doc, item, y)
    })
  }

  if (summary.openEndedItems.length > 0) {
    y = addSectionTitle(doc, 'Açık Uçlu Sorular', y)
    summary.openEndedItems.forEach((item) => {
      y = addOpenEndedTable(doc, item, y)
    })
  }

  addFooter(doc)
  doc.save(`${safeFileName(summary.surveyName)}-anket-raporu.pdf`)
}
